import React, { useCallback, useState } from 'react';
import { List, ListItem, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  useActiveDataTables,
  useDataTableIsLoading,
} from '@features/data-table/store';
import { DataTableKebabMenu } from '../DataTableKebabMenu';
import DateText from '@ui/DateText';
import EmptyState from '@ui/EmptyState';
import Loading from '@ui/Loading';

export const DataTablesList: React.FC = () => {
  const { classes } = useStyles();
  const dataTables = useActiveDataTables();
  const isLoading = useDataTableIsLoading();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleMouseEnter = useCallback((id: string) => {
    setHoveredId(id);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredId(null);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (!dataTables?.length) {
    return <EmptyState fullHeight message="No data tables found" />;
  }

  return (
    <List className={classes.list}>
      {dataTables.map((dataTable) => (
        <ListItem
          key={dataTable.id}
          className={classes.listItem}
          onMouseEnter={() => handleMouseEnter(dataTable.id)}
          onMouseLeave={handleMouseLeave}
        >
          <div className={classes.columns}>
            <div>
              <Typography variant="body2">{dataTable.name}</Typography>
            </div>
            <div>
              <DateText
                date={dataTable.updated_at}
                format={{ type: 'RELATIVE' }}
                tooltipPrefix="Updated at"
                variant="body2"
              />
            </div>
            <div>
              <DataTableKebabMenu
                dataTable={dataTable}
                hidden={hoveredId !== dataTable.id}
              />
            </div>
          </div>
        </ListItem>
      ))}
    </List>
  );
};

const useStyles = makeStyles()(({ spacing }) => ({
  list: {
    padding: 0,
  },
  listItem: {
    padding: `${spacing(2)} ${spacing(4)}`,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  columns: {
    display: 'grid',
    width: '100%',
    gridTemplateColumns: '3fr 2fr 50px',
    gap: spacing(2),
    alignItems: 'center',
    columnGap: spacing(2),
  },
}));
